import { useNavigate } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { useGame, useGameDispatch } from "../context/GameContext";
import MatchupCard from "../components/MatchupCard";
import SeedInfo from "../components/SeedInfo";
import SeoHead from "../components/SeoHead";
import BracketDisplay from "../components/BracketDisplay";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function GamePlayPage() {
  const { siteData } = useGame();
  const dispatch = useGameDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { current2Seeds, currentRound } = siteData.bracketsModeSettings;

  // If game is finished, redirect to results
  useEffect(() => {
    if (siteData.gameFinished) {
      navigate("/results");
    }
  }, [siteData.gameFinished, navigate]);

  // If no game is active, go home
  if (!siteData.gameMode || siteData.bracketSize === -1) {
    return (
      <Box id="gameplay-no-game" sx={{ textAlign: "center", mt: 8 }}>
        <Typography variant="h5" gutterBottom>
          {t("game.pickYourWinner")}
        </Typography>
        <Button variant="contained" onClick={() => navigate("/")}>
          {t("results.goHome")}
        </Button>
      </Box>
    );
  }

  if (current2Seeds.length < 2) {
    return (
      <Box id="gameplay-loading" sx={{ textAlign: "center", mt: 8 }}>
        <Typography variant="h5">Loading matchup...</Typography>
      </Box>
    );
  }

  const song1 = siteData.gameSessionSongs[current2Seeds[0]! - 1];
  const song2 = siteData.gameSessionSongs[current2Seeds[1]! - 1];

  if (!song1 || !song2) {
    return (
      <Box id="gameplay-error" sx={{ textAlign: "center", mt: 8 }}>
        <Typography variant="h5">{t("setup.failedToLoad")}</Typography>
        <Button variant="contained" onClick={() => navigate("/")} sx={{ mt: 2 }}>
          {t("results.goHome")}
        </Button>
      </Box>
    );
  }

  function handlePick(winner: number) {
    dispatch({ type: "PICK_WINNER", payload: { winner } });
  }

  return (
    <Box id="gameplay-page">
      <SeoHead title="Now Playing" description="Pick your winner in this bracket matchup." />
      <SeedInfo siteData={siteData} />
      <BracketDisplay siteData={siteData} />
      <Typography id="gameplay-round-info" variant="h5" textAlign="center" sx={{ mb: 3 }}>
        {t("game.round", { round: currentRound })} — {t("game.pickYourWinner")}
      </Typography>
      <Box
        id="gameplay-matchup-area"
        sx={{
          display: "flex",
          gap: 4,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <MatchupCard
          song={song1}
          seedNumber={current2Seeds[0]!}
          onPick={() => handlePick(current2Seeds[0]!)}
          color="#1abc9c"
        />
        <Box
          id="gameplay-vs"
          sx={{
            display: "flex",
            alignItems: "center",
            fontSize: "2rem",
            fontWeight: "bold",
            color: "text.secondary",
          }}
        >
          {t("game.vs").toUpperCase()}
        </Box>
        <MatchupCard
          song={song2}
          seedNumber={current2Seeds[1]!}
          onPick={() => handlePick(current2Seeds[1]!)}
          color="#e74c3c"
        />
      </Box>
    </Box>
  );
}
